import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state?.orderData;

  useEffect(() => {
    // If no order data, redirect to dashboard
    if (!orderData) {
      navigate('/dashboard');
    }
  }, [orderData, navigate]);

  if (!orderData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        <div className="bg-slate-800/50 backdrop-blur-lg rounded-2xl border-2 border-green-500/30 p-8 shadow-2xl">
          {/* Success Icon */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-4">
              <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-gray-400">Your order has been received and is being processed</p>
          </div>

          {/* Order Details */}
          <div className="space-y-4 mb-6">
            <div className="bg-slate-900/50 rounded-lg p-4 border border-purple-500/20">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Order ID</span>
                <span className="text-white font-mono text-sm">{orderData.orderId?.slice(0, 8)}...</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Reaction Type</span>
                <span className="text-white capitalize">{orderData.reactionType}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-400">Quantity</span>
                <span className="text-white">{orderData.quantity} reactions</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Amount Paid</span>
                <span className="text-green-400 font-semibold">{orderData.amountSol} SOL</span>
              </div>
            </div>

            {orderData.transactionSignature && (
              <div className="bg-slate-900/50 rounded-lg p-4 border border-purple-500/20">
                <div className="text-gray-400 text-sm mb-2">Transaction Signature</div>
                <a
                  href={`https://solscan.io/tx/${orderData.transactionSignature}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 hover:text-purple-300 font-mono text-xs break-all"
                >
                  {orderData.transactionSignature}
                </a>
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div className={`border rounded-lg p-4 mb-6 ${
            orderData.isPendingVerification 
              ? 'bg-orange-500/10 border-orange-500/30' 
              : 'bg-blue-500/10 border-blue-500/30'
          }`}>
            <h3 className={`font-semibold mb-2 ${
              orderData.isPendingVerification ? 'text-orange-400' : 'text-blue-400'
            }`}>
              {orderData.isPendingVerification ? '⏰ Pending Verification' : 'What\'s Next?'}
            </h3>
            {orderData.isPendingVerification ? (
              <ul className="text-sm text-gray-300 space-y-1">
                <li>✓ Your transaction has been submitted successfully</li>
                <li>⏱ <strong>Estimated verification time: 15-20 minutes</strong></li>
                <li>✓ Admin will manually verify your payment via Solscan</li>
                <li>✓ You'll receive a notification once verified</li>
                <li>✓ Track status in "My Orders" - it will update automatically</li>
              </ul>
            ) : (
              <ul className="text-sm text-gray-300 space-y-1">
                <li>✓ Your transaction has been submitted successfully</li>
                <li>✓ Admin will verify your payment manually via Solscan</li>
                <li>✓ Once verified, your order will be processed</li>
                <li>✓ You can track the status in "My Orders"</li>
              </ul>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex-1 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              View My Orders
            </button>
            <button
              onClick={() => {
                navigate('/dashboard');
                setTimeout(() => {
                  const createTab = document.querySelector('[data-tab="create"]');
                  if (createTab) createTab.click();
                }, 100);
              }}
              className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Create New Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
